import { supabase } from "../services/supabase";
import {
    CriarVendaInput,
    StatusVenda,
    Venda,
    VendaComItens,
} from "../types/database";
import { obterPeriodoMes } from "../utils/periodoMes";

const VENDA_COM_ITENS_SELECT = `
  *,
  itens_venda (
    *,
    produtos (nome)
  )
`;

const STATUS_INICIAL_VENDA: StatusVenda = "Pendente";

function sanitizarTelefone(telefone: string): string {
  const somenteDigitos = telefone.replace(/\D/g, "");
  if (somenteDigitos.length === 11) return `55${somenteDigitos}`;
  return somenteDigitos;
}

async function criarVendaNova(input: CriarVendaInput): Promise<Venda> {
  const valorTotal = input.itens.reduce(
    (total, item) => total + item.quantidade * item.preco_unitario,
    0,
  );
  const statusFinal = STATUS_INICIAL_VENDA;
  const telefonePadronizado = sanitizarTelefone(input.telefone);

  const { data: venda, error: vendaError } = await supabase
    .from("vendas")
    .insert({
      cliente_nome: input.cliente_nome.trim(),
      graduacao: input.graduacao,
      telefone: telefonePadronizado,
      observacao: input.observacao?.trim() || null,
      vencimento: input.vencimento,
      modalidade: input.modalidade?.trim() || null,
      valor_total: valorTotal,
      status: statusFinal,
      arquivado: false,
    })
    .select()
    .single();

  if (vendaError || !venda) {
    console.error("Erro ao criar venda:", vendaError);
    throw new Error(vendaError?.message ?? "Erro ao criar venda");
  }

  const itensParaInserir = input.itens.map((item) => ({
    venda_id: venda.id,
    produto_id: item.produto_id,
    nome_produto: item.nome_produto,
    quantidade: item.quantidade,
    preco_unitario: item.preco_unitario,
    subtotal: item.quantidade * item.preco_unitario,
  }));

  const { error: itensError } = await supabase
    .from("itens_venda")
    .insert(itensParaInserir);

  if (itensError) {
    console.error("Erro ao criar itens da venda:", itensError);
    await supabase.from("vendas").delete().eq("id", venda.id);
    throw new Error(itensError.message);
  }

  return venda;
}

export const vendasRepository = {
  async criar(input: CriarVendaInput): Promise<Venda> {
    return criarVendaNova(input);
  },

  async criarOuAcumular(input: CriarVendaInput): Promise<Venda> {
    const nomeCliente = input.cliente_nome.trim();
    const telefonePadronizado = sanitizarTelefone(input.telefone);
    const valorTotalItens = input.itens.reduce(
      (total, item) => total + item.quantidade * item.preco_unitario,
      0,
    );
    const statusFinal = STATUS_INICIAL_VENDA;

    const { data: vendasPendentes, error: buscaError } = await supabase
      .from("vendas")
      .select()
      .eq("status", "Pendente")
      .eq("graduacao", input.graduacao)
      .ilike("cliente_nome", nomeCliente)
      .eq("telefone", telefonePadronizado)
      .or("arquivado.is.null,arquivado.eq.false")
      .order("created_at", { ascending: false })
      .limit(1);

    if (buscaError) {
      console.error("Erro ao buscar venda pendente:", buscaError);
      throw new Error(buscaError.message);
    }

    const vendaPendente = vendasPendentes?.[0];

    if (!vendaPendente) {
      return criarVendaNova(input);
    }

    const itensParaInserir = input.itens.map((item) => ({
      venda_id: vendaPendente.id,
      produto_id: item.produto_id,
      nome_produto: item.nome_produto,
      quantidade: item.quantidade,
      preco_unitario: item.preco_unitario,
      subtotal: item.quantidade * item.preco_unitario,
    }));

    const { error: itensError } = await supabase
      .from("itens_venda")
      .insert(itensParaInserir);

    if (itensError) {
      console.error("Erro ao acumular itens na venda existente:", itensError);
      throw new Error(itensError.message);
    }

    const valorTotalAtualizado =
      Number(vendaPendente.valor_total || 0) + valorTotalItens;

    const { data: vendaAtualizada, error: atualizacaoError } = await supabase
      .from("vendas")
      .update({
        valor_total: valorTotalAtualizado,
        status: statusFinal,
        telefone: telefonePadronizado,
      })
      .eq("id", vendaPendente.id)
      .select()
      .single();

    if (atualizacaoError || !vendaAtualizada) {
      console.error(
        "Erro ao atualizar total da venda pendente:",
        atualizacaoError,
      );
      throw new Error(
        atualizacaoError?.message ?? "Erro ao atualizar total da venda",
      );
    }

    return vendaAtualizada;
  },

  async listarTodas(): Promise<VendaComItens[]> {
    const { data, error } = await supabase
      .from("vendas")
      .select(VENDA_COM_ITENS_SELECT)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Erro ao listar vendas:", error);
      throw new Error(error.message);
    }

    return data ?? [];
  },

  async listarDoMesParaResumo(
    inicioMesIso: string,
    fimMesIso: string,
  ): Promise<VendaComItens[]> {
    const { data, error } = await supabase
      .from("vendas")
      .select(VENDA_COM_ITENS_SELECT)
      .gte("created_at", inicioMesIso)
      .lte("created_at", fimMesIso)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Erro ao listar vendas do período para resumo:", error);
      throw new Error(error.message);
    }

    return data ?? [];
  },

  async listarDoMesAtualParaResumo(): Promise<VendaComItens[]> {
    const periodo = obterPeriodoMes(new Date());

    return this.listarDoMesParaResumo(
      periodo.inicioIso,
      periodo.fimIso,
    );
  },

  async listarPendentes(): Promise<VendaComItens[]> {
    const { data, error } = await supabase
      .from("vendas")
      .select(VENDA_COM_ITENS_SELECT)
      .eq("status", "Pendente")
      .or("arquivado.is.null,arquivado.eq.false")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Erro ao listar vendas pendentes:", error);
      throw new Error(error.message);
    }

    return data ?? [];
  },

  async atualizarStatus(id: string, status: StatusVenda): Promise<Venda> {
    const { data, error } = await supabase
      .from("vendas")
      .update({ status })
      .eq("id", id)
      .select()
      .single();

    if (error || !data) {
      console.error("Erro ao atualizar status da venda:", error);
      throw new Error(error?.message ?? "Erro ao atualizar status");
    }

    return data;
  },

  async deletarVenda(id: string): Promise<void> {
    const { error } = await supabase
      .from("vendas")
      .update({ arquivado: true })
      .eq("id", id);

    if (error) {
      console.error("Erro ao arquivar venda:", error);
      throw new Error(error.message);
    }
  },

  async arquivarTodosPagos(): Promise<void> {
    const { error } = await supabase
      .from("vendas")
      .update({ arquivado: true })
      .eq("status", "Pago")
      .or("arquivado.is.null,arquivado.eq.false");

    if (error) {
      console.error("Erro ao arquivar todas as vendas pagas:", error);
      throw new Error(error.message);
    }
  },
};
